import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import { create_event, list_events, useEventsRequest } from './events_api';

/**
 * Events list — /events
 *
 * A topic has no status, so there is no status filter. Sorting and the dormancy
 * em-dash are both decided server-side: next_run_date comes back null when nothing
 * the landing page would actually serve is upcoming, and the rows arrive already
 * ordered. This screen renders that decision rather than recomputing it, so
 * Hridaya and the landing page cannot disagree.
 */

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// A suggestion rather than a rule; the host can overwrite it.
const slugify = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default function Events({ history }) {
  const [topics, setTopics] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slug_touched, setSlugTouched] = useState(false);
  const { run, loading } = useEventsRequest();

  const load = useCallback(async () => {
    const { data } = await run(() => list_events());
    if (data) setTopics(data.data.topics || []);
  }, [run]);

  useEffect(() => {
    load();
  }, [load]);

  const open_create = () => {
    setName('');
    setSlug('');
    setSlugTouched(false);
    setCreating(true);
  };

  const on_name_change = (value) => {
    setName(value);
    // Stop suggesting once the host has edited the slug themselves.
    if (!slug_touched) setSlug(slugify(value));
  };

  const slug_error = (() => {
    if (!slug) return '';
    if (!SLUG_PATTERN.test(slug)) return 'Lowercase letters, numbers and hyphens only';
    return '';
  })();

  const submit_create = async () => {
    const { data } = await run(() => create_event({ name: name.trim(), slug: slug.trim() }));
    if (!data) return;
    setCreating(false);
    // Straight to the new topic, since the next thing a host wants is to add a run.
    history.push(`/events/${slug.trim()}`);
  };

  const can_create = !!name.trim() && !!slug.trim() && !slug_error && !loading;

  return (
    <CommonHeader title="Events" loading={loading}>
      <div className="p-4">
        <div className="flex items-center mb-4">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Events
          </h2>
          <button
            type="button"
            onClick={open_create}
            className="ml-auto px-4 py-2 rounded font-medium"
            style={{ background: 'var(--brand-primary)', color: 'var(--text-inverse)' }}
          >
            + Create Event
          </button>
        </div>

        {!loading && topics.length === 0 ? (
          <div
            className="rounded-lg p-8 text-center"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              color: 'var(--text-muted)'
            }}
          >
            No events yet
          </div>
        ) : (
          <div
            className="rounded-lg overflow-hidden"
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              boxShadow: 'var(--shadow-card)'
            }}
          >
            <div
              className="grid gap-4 px-4 py-3 text-xs font-semibold uppercase tracking-wide"
              style={{
                gridTemplateColumns: '2fr 2fr 80px 160px',
                color: 'var(--text-secondary)',
                background: 'var(--surface-sunken)',
                borderBottom: '1px solid var(--border-default)'
              }}
            >
              <div>Name</div>
              <div>Slug</div>
              <div>Runs</div>
              <div>Next run</div>
            </div>

            {topics.map((topic) => (
              <button
                key={topic.topic_uuid}
                type="button"
                onClick={() => history.push(`/events/${topic.slug}`)}
                className="grid gap-4 px-4 py-3 w-full text-left"
                style={{
                  gridTemplateColumns: '2fr 2fr 80px 160px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 0,
                  borderBottom: '1px solid var(--border-default)',
                  color: 'var(--text-primary)'
                }}
              >
                <div>{topic.name}</div>
                <div style={{ color: 'var(--text-muted)' }}>{topic.slug}</div>
                <div>{topic.run_count}</div>
                <div>
                  {topic.next_run_date
                    ? moment(topic.next_run_date, 'YYYY-MM-DD').format('D MMM YYYY')
                    : '—'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <CustomModal show={creating} close={() => setCreating(false)} title="Create event">
        <div className="p-2" style={{ minWidth: 360 }}>
          <label
            className="block text-sm mb-1"
            style={{ color: 'var(--text-secondary)' }}
            htmlFor="event-name"
          >
            Name
            <input
              id="event-name"
              value={name}
              onChange={(e) => on_name_change(e.target.value)}
              className="w-full px-2 py-1 rounded mb-3"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                color: 'var(--input-text)'
              }}
            />
          </label>

          <label
            className="block text-sm mb-1"
            style={{ color: 'var(--text-secondary)' }}
            htmlFor="event-slug"
          >
            Slug
            <input
              id="event-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              className="w-full px-2 py-1 rounded"
              style={{
                background: 'var(--input-bg)',
                border: `1px solid ${slug_error ? 'var(--state-error)' : 'var(--input-border)'}`,
                color: 'var(--input-text)'
              }}
            />
          </label>
          <div
            className="mt-1 text-xs"
            style={{ color: slug_error ? 'var(--state-error-text)' : 'var(--text-muted)' }}
          >
            {slug_error || 'Cannot be changed after creation — the landing page joins on it.'}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="ct-small-btn" onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button
              type="button"
              onClick={submit_create}
              disabled={!can_create}
              className="px-3 py-1 rounded font-medium"
              style={{
                background: can_create ? 'var(--brand-primary)' : 'var(--border-emphasis)',
                color: 'var(--text-inverse)',
                cursor: can_create ? 'pointer' : 'not-allowed'
              }}
            >
              Create
            </button>
          </div>
        </div>
      </CustomModal>
    </CommonHeader>
  );
}

Events.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func }).isRequired
};
